import Button from 'components/Basic/Button';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { useMutation } from 'react-query';
import { adjustShadingInit, undoRemoveShading } from '../api';
import { openShadingMenu } from 'store/slices/AdjustShadingSlice';
import { setEditShadingPerfomed } from 'store/slices/ToolSlice';
import { changePanel } from 'store/slices/RoofDataSlice/roofDataSlice';
import { appToast } from 'components/Basic/CustomToast';
import { AxiosError } from 'axios';
import { EnergyState } from 'store/slices/EnergySlice/types';
import { getDeratedValue } from '../../../../../util';
import { TOOL_ACTION } from '../../../../../store/selectors/toolSelector';
import { clearAction, registerActionHandler } from '../../ToolRegistry';

type Props = {
  isMobileView: boolean;
  disableRemoveShading: boolean;
  removeShadingQueryInProgress: boolean;
  facet?: RasterRoofSegment;
};

export default function RemoveShadingButton({
	isMobileView,
	disableRemoveShading,
	removeShadingQueryInProgress,
	facet,
}: Props) {
	const dispatch = useDispatch<AppDispatch>();
	const allowedActions = useSelector(
		(state: RootState) => state.roleState.data.allowedActions
	);
	const { uuid, panel, designType } = useSelector((state: RootState) => state.roofData.data);
	const { selectedPanels: selectedPanelsOnUI = [], deratedBy } = useSelector(
		(state: RootState) => (state.energyData.data ?? {}) as EnergyState
	);
	const isPatched = !!facet?.isFluxPatched;
	const { totalSelectedPanelsFromFacet, totalSelectedPanelsProduction } = (facet?.panels ?? []).reduce((sum, panel) => ({
		totalSelectedPanelsFromFacet: sum.totalSelectedPanelsFromFacet + (selectedPanelsOnUI.includes(panel.id) ? 1 : 0),
		totalSelectedPanelsProduction: sum.totalSelectedPanelsProduction + (selectedPanelsOnUI.includes(panel.id) ? panel.yearlyEnergyDcKwh : 0)
	}), { totalSelectedPanelsFromFacet: 0, totalSelectedPanelsProduction: 0 });
	const { totalPanelsFromFacet, totalPanelsProduction } = (facet?.panels ?? []).reduce((sum, panel) => ({
		totalPanelsFromFacet: sum.totalPanelsFromFacet + 1,
		totalPanelsProduction: sum.totalPanelsProduction + panel.yearlyEnergyDcKwh
	}), { totalPanelsFromFacet: 0, totalPanelsProduction: 0 });
	const azimuth = facet?.azimuthDegrees || 0;
	const pitch = facet?.pitchDegrees || 0;
	const shadingActionType = isPatched ? (facet?.isShadingAdjusted ? 'adjust' : 'undo') : (facet?.isShadingAdjusted ? 'adjust' : 'init');
	const canUndo = allowedActions.includes('undo-remove-roof-segment-shading');

	const removeShadingQuery = useMutation( async () => {
		if (!facet) return;
		const payload = {
			facetId: facet.id as string,
			panelKey: panel.key,
			isUserMapped: !!facet.userMapped,
			shading: Math.round(facet?.shading || 0),
		};
		const request$ = shadingActionType === 'undo' ? undoRemoveShading : adjustShadingInit;
		if(shadingActionType === 'adjust'){
			dispatch(openShadingMenu(
				{
					azimuth: azimuth,
					pitch: pitch,
					systemProduction: getDeratedValue(totalSelectedPanelsProduction, deratedBy),
					panelsCount: totalSelectedPanelsFromFacet,
					systemSize: totalSelectedPanelsFromFacet * panel.wattage,
					segmentId: facet.id, 
					shadingVal: facet?.shading ?? 0, 
					isUserMapped: !!facet?.userMapped, 
					panelKey: panel.key,
					isShadingAdjusted: !!facet?.isShadingAdjusted,
					doneEnabled: false,
					totalPanelsProduction: getDeratedValue(totalPanelsProduction, deratedBy),
					totalSystemSize: totalPanelsFromFacet * panel.wattage,
					totalPanelsFromFacet: totalPanelsFromFacet,
				}
			));
			return;
		}
		const reponse = await request$(uuid, payload);
		dispatch(setEditShadingPerfomed({editShadingPerformed: true}));
		await dispatch(changePanel({ uuid, panelKey: panel.key, designType:designType }));
		dispatch(setEditShadingPerfomed({editShadingPerformed: false}));
		return {shading: reponse.data.shading, isUserMapped: !!facet?.userMapped, facetId: facet.id};
	},
	{
		mutationKey: 'remove-shading',
		onSuccess(reponse) {
			if(!reponse)	return;
			const {shading, isUserMapped, facetId} = reponse;
			if(shadingActionType === 'init'){
				dispatch(openShadingMenu({
					azimuth: azimuth,
					pitch: pitch,
					systemProduction: getDeratedValue(totalSelectedPanelsProduction, deratedBy),
					panelsCount: totalSelectedPanelsFromFacet,
					systemSize: totalSelectedPanelsFromFacet * panel.wattage,
					segmentId: facetId,
					shadingVal: shading, 
					isUserMapped: isUserMapped,
					panelKey: panel.key,
					isShadingAdjusted: !!facet?.isShadingAdjusted,
					doneEnabled: true,
					totalPanelsProduction: getDeratedValue(totalPanelsProduction, deratedBy),
					totalSystemSize: totalPanelsFromFacet * panel.wattage,
					totalPanelsFromFacet: totalPanelsFromFacet,
				}));
			}
		},
		onError(error: AxiosError<{ message: string }>) {
			if (error.response?.data) {
				appToast.error(error.response.data?.message || error.message);
			}
		}
	});

	const handleClick = useCallback(() => {
		if (!disableRemoveShading) {
			// dispatch(
			// 	toggleShadingModalState({
			// 		value: true,
			// 		type: shadingActionType,
			// 	})
			// );
			removeShadingQuery.mutate();
		}

	}, [disableRemoveShading, removeShadingQuery]);

	useEffect(() => {
		registerActionHandler(TOOL_ACTION.REMOVE_SHADING, handleClick);
		return () => {
			clearAction(TOOL_ACTION.REMOVE_SHADING);
		};
	}, [handleClick]);

	return (
		<Button
			id={!isPatched ? 'remove-shading' : 'undo-remove-shading'}
			className="btn-tools"
			style={{ background: 'var(--defaultBgColor)' }}
			disabled={disableRemoveShading}
			onClick={handleClick}
			tooltipContent={shadingActionType === 'undo' ? 'Undo Remove Shading' : 'Adjust Shading'}
			showLoader={removeShadingQueryInProgress}
		>
			{/* same icon logic you had, but reads undo permission from store */}
			{(!isPatched && canUndo) ? (
				<svg
					width={isMobileView ? '17' : '19'}
					height={isMobileView ? '21' : '23.75'}
					viewBox="0 0 20 25"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path d="M17.0642 12.6377C17.9258 17.5208 12.4683 19.5315 10.0268 16.5154C6.00538 19.9623 1.98402 16.3718 2.84584 12.6377C1.12845 10.7771 0.628835 8.48752 1.26624 6.74929C1.90804 4.99908 3.70019 3.80582 5.86186 3.58966C7.58543 0.429882 11.6068 -0.144401 14.1918 3.58966C16.0904 3.43788 18.1401 5.06983 18.7878 7.18035C19.365 9.06129 18.7563 11.3519 17.0642 12.6377Z" stroke="var(--defaultIconColor)" strokeWidth="1.69284" strokeLinecap="round" strokeLinejoin="round"/>
					<path d="M10.0288 7.03809V11.6339M10.0288 23.4108V14.6499M10.0288 11.6339L13.332 9.62324M10.0288 11.6339V14.6499M10.0288 14.6499L6.86914 12.7829" stroke="var(--defaultIconColor)" strokeWidth="1.69284" strokeLinecap="round" strokeLinejoin="round"/>
				</svg>
			) : (
				<svg
					width={isMobileView ? '17' : '18'}
					height={isMobileView ? '21' : '21'}
					viewBox="0 0 18 21"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path d="M14.6516 10.8899C15.3838 15.0396 10.7459 16.7483 8.67114 14.1852C5.25364 17.1144 1.83623 14.0632 2.56862 10.8899C1.10916 9.30868 0.684579 7.363 1.22626 5.88582C1.77166 4.39847 3.29466 3.38443 5.13168 3.20073C6.59639 0.515506 10.0138 0.027472 12.2106 3.20073C13.824 3.07174 15.5659 4.4586 16.1163 6.25215C16.6069 7.85059 16.0895 9.79718 14.6516 10.8899Z" stroke="var(--defaultIconColor)" strokeWidth="1.69284" strokeLinecap="round" strokeLinejoin="round"/>
					<path d="M8.67046 6.13123V10.0368M8.67046 20.045V12.5999M8.67046 10.0368L11.4776 8.32813M8.67046 10.0368V12.5999M8.67046 12.5999L5.98535 11.0132" stroke="var(--defaultIconColor)" strokeWidth="1.69284" strokeLinecap="round" strokeLinejoin="round"/>
				</svg>
			)}
		</Button>
	);
}
